import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Flex, HStack, IconButton, Image, Modal, ModalContent, ModalOverlay,
  Slider, SliderFilledTrack, SliderThumb, SliderTrack, Stack, Text, useToast,
} from '@chakra-ui/react';
import { ID3Writer } from 'browser-id3-writer';
import { RootState } from '../redux/store';
import {
  collapse, cycleRepeat, next, pause, play, prev, seek, toggleShuffle,
} from '../redux/features/playerSlice';

/**
 * Full-screen "now playing" view — SoundCloud / Spotify / stock Android
 * music-player conventions: giant cover art, big transport row with
 * repeat + shuffle flanking prev/play/next, seek bar with tabular-num
 * times, an actions row (favourite, share, download) at the bottom, and
 * a swipe-down / X to collapse back to the sticky bar.
 *
 * The component is a Chakra Modal (full-viewport, no card chrome) mounted
 * globally alongside the sticky bar. Redux state (`player.expanded`) is
 * the single source of truth for open/closed — tapping the sticky bar
 * toggles it.
 */
export function FullPlayer() {
  const dispatch = useDispatch();
  const toast = useToast();
  const { queue, index, isPlaying, progress, duration, expanded, repeatMode, shuffle } =
    useSelector((s: RootState) => s.player);
  const track = index >= 0 ? queue[index] : null;

  // Swipe-down to close on touch devices. Keep it minimal — a real
  // gesture library is overkill for one interaction.
  const close = () => dispatch(collapse());

  // Auto-close if the queue empties while the full view is up.
  useEffect(() => {
    if (expanded && !track) close();
  }, [expanded, track]);

  if (!track) return null;
  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  const downloadWithTags = async () => {
    if (!track.audioUrl) return;
    try {
      const res = await fetch(track.audioUrl);
      if (!res.ok) throw new Error(`fetch ${res.status}`);
      const buf = await res.arrayBuffer();
      const writer = new ID3Writer(buf);
      writer.setFrame('TIT2', track.title).setFrame('TPE1', [track.artist || 'MixGenie']).setFrame('TALB', 'MixGenie');
      writer.addTag();
      const blob = writer.getBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(track.title || 'song').replace(/[^\w\-. ()]+/g, '_').slice(0, 80)}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Download failed', status: 'error', duration: 2500 });
    }
  };

  const share = async () => {
    if (!navigator.share) {
      toast({ title: 'Sharing not available on this device', status: 'info', duration: 2000 });
      return;
    }
    try {
      await navigator.share({
        title: track.title,
        text: `${track.title} — ${track.artist || 'MixGenie'}`,
        url: window.location.origin + '/songs',
      });
    } catch { /* user cancelled */ }
  };

  return (
    <Modal isOpen={expanded} onClose={close} size="full" motionPreset="slideInBottom">
      <ModalOverlay />
      <ModalContent
        bg="night.900"
        color="whiteAlpha.900"
        m={0}
        borderRadius={0}
        overflow="hidden"
      >
        <Flex direction="column" h="100dvh" px={5} pt={4} pb={{ base: 6, md: 10 }}>
          {/* Top bar */}
          <Flex justify="space-between" align="center" pb={2}>
            <IconButton
              aria-label="Close player"
              icon={<ChevronDown />}
              onClick={close}
              variant="ghost"
              color="whiteAlpha.800"
              size="lg"
            />
            <Stack spacing={0} textAlign="center" flex="1" px={2} minW={0}>
              <Text fontSize="10px" letterSpacing="0.16em" fontWeight={600} color="whiteAlpha.500" textTransform="uppercase">
                Now playing
              </Text>
              <Text fontSize="sm" color="whiteAlpha.700" noOfLines={1}>MixGenie</Text>
            </Stack>
            <Box w="40px" />
          </Flex>

          {/* Cover — expands to fill available height/width */}
          <Flex flex="1" align="center" justify="center" py={{ base: 4, md: 8 }}>
            <Box
              maxW={{ base: '75vw', md: '360px' }}
              w="100%"
              aspectRatio={1}
              borderRadius="18px"
              overflow="hidden"
              boxShadow="0 20px 60px rgba(0,0,0,0.6)"
              position="relative"
            >
              {track.coverUrl ? (
                <Image src={track.coverUrl} alt="" w="100%" h="100%" objectFit="cover" />
              ) : (
                <Flex
                  w="100%"
                  h="100%"
                  bgGradient="linear(135deg, pink.500, purple.600)"
                  align="center"
                  justify="center"
                  color="white"
                  fontSize={{ base: '80px', md: '110px' }}
                >
                  🎵
                </Flex>
              )}
            </Box>
          </Flex>

          {/* Title + artist */}
          <Stack spacing={1} textAlign="center" px={2} mb={4}>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              fontWeight={700}
              color="whiteAlpha.900"
              noOfLines={1}
              px={2}
            >
              {track.title}
            </Text>
            <Text fontSize="sm" color="whiteAlpha.600" noOfLines={1}>
              {track.artist || 'MixGenie'}
            </Text>
          </Stack>

          {/* Seek bar */}
          <HStack spacing={3} px={2} mb={3} color="whiteAlpha.600" fontSize="xs">
            <Text w="42px" textAlign="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmt(progress)}
            </Text>
            <Slider
              aria-label="Seek"
              value={pct}
              min={0}
              max={100}
              onChange={(v) => {
                if (!duration) return;
                const t = (v / 100) * duration;
                // Playback element lives in GlobalAudioPlayer; dispatch a
                // seek that GAP's effect picks up and applies to audio.
                dispatch(seek(t));
                const audio = document.querySelector('audio') as HTMLAudioElement | null;
                if (audio) audio.currentTime = t;
              }}
              focusThumbOnChange={false}
              flex="1"
            >
              <SliderTrack bg="whiteAlpha.300" h="4px">
                <SliderFilledTrack bg="pink.400" />
              </SliderTrack>
              <SliderThumb boxSize={4} bg="pink.400" />
            </Slider>
            <Text w="42px" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmt(duration)}
            </Text>
          </HStack>

          {/* Transport row */}
          <Flex align="center" justify="space-between" px={2} mb={6}>
            <IconButton
              aria-label="Repeat"
              icon={<RepeatIcon mode={repeatMode} />}
              onClick={() => dispatch(cycleRepeat())}
              variant="ghost"
              color={repeatMode === 'off' ? 'whiteAlpha.500' : 'pink.400'}
              size="lg"
            />
            <IconButton
              aria-label="Previous"
              icon={<TransportGlyph d="M6 6h2v12H6zM9.5 12l8.5 6V6z" />}
              onClick={() => dispatch(prev())}
              variant="ghost"
              color="whiteAlpha.900"
              size="lg"
              fontSize="28px"
              isDisabled={index <= 0}
            />
            <IconButton
              aria-label={isPlaying ? 'Pause' : 'Play'}
              icon={isPlaying
                ? <TransportGlyph d="M6 5h4v14H6zM14 5h4v14h-4z" />
                : <TransportGlyph d="M8 5v14l11-7z" />
              }
              onClick={() => dispatch(isPlaying ? pause() : play())}
              bg="pink.500"
              color="white"
              _hover={{ bg: 'pink.400' }}
              _active={{ bg: 'pink.600' }}
              borderRadius="full"
              boxSize="70px"
              fontSize="34px"
            />
            <IconButton
              aria-label="Next"
              icon={<TransportGlyph d="M6 18l8.5-6L6 6v12zM16 6h2v12h-2z" />}
              onClick={() => dispatch(next())}
              variant="ghost"
              color="whiteAlpha.900"
              size="lg"
              fontSize="28px"
              isDisabled={index >= queue.length - 1}
            />
            <IconButton
              aria-label="Shuffle"
              icon={<ShuffleIcon />}
              onClick={() => dispatch(toggleShuffle())}
              variant="ghost"
              color={shuffle ? 'pink.400' : 'whiteAlpha.500'}
              size="lg"
            />
          </Flex>

          {/* Actions row — mirrors the reference: share · download · queue */}
          <Flex justify="space-around" pb={{ base: 4, md: 6 }} color="whiteAlpha.700">
            <IconButton
              aria-label="Share"
              icon={<GlyphShare />}
              onClick={share}
              variant="ghost"
              size="md"
              _hover={{ color: 'pink.300' }}
            />
            <IconButton
              aria-label="Download"
              icon={<GlyphDownload />}
              onClick={downloadWithTags}
              variant="ghost"
              size="md"
              _hover={{ color: 'pink.300' }}
            />
            <IconButton
              aria-label="Queue"
              icon={<GlyphQueue />}
              onClick={() => toast({ title: `${queue.length} in queue`, description: `Playing ${index + 1} of ${queue.length}`, status: 'info', duration: 2000 })}
              variant="ghost"
              size="md"
              _hover={{ color: 'pink.300' }}
            />
          </Flex>
        </Flex>
      </ModalContent>
    </Modal>
  );
}

// ---------- Glyphs (inline SVG so no icon-font/CDN dependency) ---------

function TransportGlyph({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" style={{ display: 'block' }}>
      <path d={d} />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M7 10l5 5 5-5z" /></svg>
  );
}

function RepeatIcon({ mode }: { mode: 'off' | 'one' | 'all' }) {
  // Same glyph for all three; "one" gets a small "1" badge overlay.
  return (
    <Box position="relative">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
        <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" />
      </svg>
      {mode === 'one' && (
        <Text
          position="absolute"
          top="-4px"
          right="-6px"
          fontSize="9px"
          fontWeight={800}
          bg="pink.400"
          color="white"
          borderRadius="full"
          w="14px"
          h="14px"
          lineHeight="14px"
          textAlign="center"
        >
          1
        </Text>
      )}
    </Box>
  );
}

function ShuffleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
      <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z" />
    </svg>
  );
}

function GlyphShare() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.15c-.05.21-.08.43-.08.66 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
    </svg>
  );
}

function GlyphDownload() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
    </svg>
  );
}

function GlyphQueue() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
      <path d="M4 10h12v2H4zM4 6h12v2H4zM4 14h8v2H4zM14 14v6l5-3z" />
    </svg>
  );
}

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
